
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form, Modal, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { withTranslation } from 'react-i18next';
import { BiCopy, BiDotsVerticalRounded, BiHelpCircle, BiTrash, BiPencil } from "react-icons/bi";
import { BsFileEarmarkFill, BsPaperclip, BsPencil, BsX } from "react-icons/bs";
import { FcFinePrint } from "react-icons/fc";
import { MdAddCircleOutline } from "react-icons/md";
import { toast } from "react-toastify";
import { LoaderContext, PreferenceContext, RegulationContext } from "../../../App";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import useHasPermission from "../../../core/hooks/useHasPermission";
import CompanyService from "../../../core/service/CompanyService";
import GeneralDisclosureService from "../../../core/service/GeneralDisclosureService";
import StandardService from "../../../core/service/StandardService";
import Utils from "../../../core/utilities/Utils";
import esg_ai from '../../../images/esg_ai.png';
import GDQuestionFormPopup from "../../Common/Disclosures/GDQuestionFormPopup";
import commentAttached from "../../../images/comment_attach.png";
import './EnterpriseStructure.scss';
const CompanyQuestionContainer = ({
  t,
  type = 'MATERIAL_TOPIC',
  filter,
  questions,
  ansData,
  helpTextId,
  onRefineWithAIClick,
  onDisplayHelpText,
  refreshData,
  pgnum,
  changeQuestionStatus,
  isQuestionEditable = false
}) => {

  const [answers, setAnswers] = useState({});
  const [editedQuestion, setEditedQuestion] = useState({});
  const [editedHelpText, setEditedHelpText] = useState({});
  const questionRefs = [];
  const [textEntered, setTextEntered] = useState(false);
  const [repeatClicked, setRepeatClicked] = useState(false);
  const [quesOpenedForUpdate, setQuesOpenedForEdit] = useState({});
  const [replicatedSubQuestionGroups, setReplicatedSubQuestionGroups] = useState({});
  const [pendingReplicateAnswer, setPendingReplicateAnswer] = useState(null);
  const [commentsAttachmentsFor, setCommentsAttachmentsFor] = useState(null);
  const [isRemarkEdit, setIsRemarkEdit] = useState(false);
  const [showRemarkAttachmentConfirmation, setShowRemarkAttachmentConfirmation] = useState(false);
  const [selectedRemarkAttachmentId, setSelectedRemarkAttachmentId] = useState(null);
  const [tempRemark, setTempRemark] = useState('');
  const [tempRemarkAttachments, setTempRemarkAttachments] = useState([]);
  const remarkFileInputRef = useRef();
  const { preference } = useContext(PreferenceContext);
  const { setLoaderDisplay } = useContext(LoaderContext);
  const { glbRegulation } = useContext(RegulationContext);
  const glbRegulationId = glbRegulation?.id ?? "";
  const { hasDMOneTimeUpdatePermission } = useHasPermission();

  useEffect(() => {
    setAnswers(ansData);
  }, [ansData])

  useEffect(() => {
    if (helpTextId) return;

    setEditedHelpText(null);
  }, [helpTextId]);


  const renderQuestion = ({
    question,
    index,
    isSubQuestion = false,
    answerIndex,
    qRef,
    parentQuestion = null,
    rowCell = undefined,
    showReplicateAction = false
  }) => {

    const hasQuestion = type === 'MATERIAL_TOPIC'
      ? Utils.isNotEmpty(question.question)
      : Utils.isNotEmpty(question.name);
    const hasSampleAnswer = type === 'MATERIAL_TOPIC' ? Utils.isNotEmpty(question.sampleAnswer) : false;
    if (!hasQuestion && !question.helpText && !hasSampleAnswer) {
      return <></>;
    }
    const hasSubQuestions = question.children?.length > 0;
    let questionName = type === 'MATERIAL_TOPIC' ? question.question : question.name;

    if (question.localizedContents && question.localizedContents.length > 0) {
      const translatedDetailsArr = question.localizedContents.find(item => item.fieldName === 'question' && item.languageCode === preference.language);

      if (translatedDetailsArr) {
        questionName = translatedDetailsArr.translatedValue;
      }
    }

    let qBoxClass = '';
    if ((editedQuestion[question.id] && editedQuestion[question.id][answerIndex ?? 0] === true)
      || (editedHelpText?.qId === question.id && editedHelpText?.aId === (answerIndex ?? 0))) {
      qBoxClass = 'text-primary';
    } else if (hasSubQuestions && question.children.every(subQuestion => answers[subQuestion.id]?.every(a => a && a.answer))) {
      qBoxClass = 'qbox-questions'; // Highlight if all subquestions are answered
    } else if (answers[question.id] && answers[question.id].every(a => a && a.answer)) {
      qBoxClass = 'qbox-questions'; // Highlight completed questions

    }

    let totalSubQuestions = 0;
    let answeredSubQuestions = 0;
    if (hasSubQuestions) {
      totalSubQuestions = question.children.length;
      for (const subQuestion of question.children) {
        const subQuestionAnswer = answers[subQuestion.id]?.find(a => a && a.answer);
        if (subQuestionAnswer) {
          answeredSubQuestions++;
        }
      }
    }

    return (
      <div id={`qContainer-${question.id}-${index}`}
        key={`qContainer-${question.id}${index}`}
        className="metriTopicQus-container"
        ref={qRef}>
        {
          hasQuestion &&
          <div className={`metricTopicQus_box ${qBoxClass}`}>
            <span className='Qus_Count'>

              {`${index + 1}.`}

            </span>
            <h6 className="d-flex align-items-center">
              <span className="fw-600">
                {hasQuestion && ` ${questionName}`}
              </span>
              {hasSubQuestions && (
                <>
                  {hasSubQuestions && (
                    <><div className="total_count_Q">
                      <span>
                        {answeredSubQuestions}
                      </span>
                      /
                      <span>
                        {totalSubQuestions}
                      </span >
                      <span className="ps-2 Answered">Answered</span>
                    </div>
                    </>
                  )}
                </>
              )}
              <div className="Qus_text_icon">
                <ul>
                  {!isSubQuestion && isQuestionEditable && renderQuestionOptions(question)}
                  {
                    question.helpText &&
                    <li>
                      <span className='cursor' onClick={() => {
                        const rectTop = qRef.current?.getBoundingClientRect()?.top;
                        const qClone = {
                          ...question,
                          top: rectTop !== undefined ? (window.scrollY + rectTop - 150) : 0
                        };
                        setEditedHelpText({ qId: question.id, aId: answerIndex ?? 0 });
                        onDisplayHelpText(qClone);
                      }}>
                        <BiHelpCircle />
                      </span>
                    </li>
                  }
                  <li>
                    <div>
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Tooltip>
                            {question.disabled ? t('QuestionNAOff.label') : t('QuestionNAOn.label')}
                          </Tooltip>
                        }
                      >
                        <Form.Check
                          type="switch"
                          variant="outline-primary"
                          className="ps-0"
                          checked={question.disabled}
                          onChange={() => enabledOrDisableAnswering(question.id, answerIndex, !question.disabled)}
                        />
                      </OverlayTrigger>
                    </div>
                  </li>
                </ul>
              </div>
            </h6>
          </div>
        }
        {
          hasSubQuestions
            ? renderChildrenQuestions(question, index)
            : renderAnswerContainer({
              question: question,
              questionIndex: index,
              isSubQuestion: isSubQuestion,
              answerIndex: answerIndex ?? 0,
              rowCell: rowCell,
              parentQuestion: parentQuestion,
              showReplicateAction: showReplicateAction
            })
        }

      </div>
    );
  };

  const renderSubQuestionColumnHeader = (question, qRef) => {
    let questionName = type === 'MATERIAL_TOPIC' ? question.question : question.name;

    if (question.localizedContents && question.localizedContents.length > 0) {
      const translatedDetailsArr = question.localizedContents.find(item => item.fieldName === 'question' && item.languageCode === preference.language);

      if (translatedDetailsArr) {
        questionName = translatedDetailsArr.translatedValue;
      }
    }

    return (
      <div className="subquestion-column-header ">
        <div className="fw-600">{questionName}</div>
        <ul className="Qus_text_icon list-unstyled  mb-0">
          {
            question.helpText &&
            <li>
              <span className='cursor' onClick={() => {
                const rectTop = qRef?.current?.getBoundingClientRect()?.top;
                const qClone = {
                  ...question,
                  top: rectTop !== undefined ? (window.scrollY + rectTop - 150) : 0
                };
                setEditedHelpText({ qId: question.id, aId: 0 });
                onDisplayHelpText(qClone);
              }}>
                <BiHelpCircle />
              </span>
            </li>
          }
          <li>
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip>
                  {question.disabled ? t('QuestionNAOff.label') : t('QuestionNAOn.label')}
                </Tooltip>
              }
            >
              <Form.Check
                type="switch"
                variant="outline-primary"
                className="ps-0 mb-0"
                checked={question.disabled}
                onChange={() => enabledOrDisableAnswering(question.id, 0, !question.disabled)}
              />
            </OverlayTrigger>
          </li>

        </ul>
      </div>
    );
  };

  const renderChildrenQuestions = (question, index) => {
    const rowCellsSet = new Set();
    question.children?.forEach(q => {
      answers[q.id]?.forEach(a => {
        if (a && a.cell !== undefined && a.cell !== null) rowCellsSet.add(`${a.cell}`);
      });
    });
    const rowCells = Array.from(rowCellsSet).sort((a, b) => +a - +b);
    if (rowCells.length === 0) rowCells.push('1');

    const canReplicate = question.children.length > 2;
    const canRepeat = question.children?.filter(q => q.singleAnswer === false)?.length > 0;

    if (!canRepeat) {
      // Not a repeatable subquestion set — keep the original stacked layout.
      return (
        <div key={`childrenQuestions-${question.id}-${index}`}
          id={`childrenQuestions-${question.id}-${index}`} >
          {
            rowCells.map((rowCell, rowIndex) => {
              return (
                <div key={`subQuestionHolder-${question.id}-${index}-${rowIndex}`} className="childrenQuestions_box">
                  {
                    question.children?.map((q, subQIndex) => {
                      const qRef = createRef();
                      questionRefs.push(qRef);
                      const childAnswers = answers[q.id] || [];
                      let answerIndex = childAnswers.findIndex(a => a && `${a.cell}` === rowCell);
                      if (answerIndex === -1) answerIndex = childAnswers.length;
                      return renderQuestion({
                        question: q,
                        index: subQIndex,
                        isSubQuestion: true,
                        answerIndex: answerIndex,
                        qRef: qRef,
                        parentQuestion: question,
                        rowCell: rowCell,
                        showReplicateAction: subQIndex === 0 && canReplicate
                      });
                    })
                  }
                </div>
              );
            })
          }
        </div>
      );
    }

    return (
      <div key={`childrenQuestions-${question.id}-${index}`}
        id={`childrenQuestions-${question.id}-${index}`}
        className="childrenQuestions_table_box">
        <div className="table-responsive">
          <Table bordered className="subquestion-table align-middle mb-2">
            <thead>
              <tr>
                {
                  question.children?.map((q) => {
                    const qRef = createRef();
                    questionRefs.push(qRef);
                    return (
                      <th key={`subq-header-${question.id}-${q.id}`} ref={qRef}>
                        {renderSubQuestionColumnHeader(q, qRef)}
                      </th>
                    );
                  })
                }
                <th className="text-center subquestion-table-actions-header">{t('Actions.label')}</th>
              </tr>
            </thead>
            <tbody>
              {
                rowCells.map((rowCell, rowIndex) => (
                  <tr key={`subQuestionRow-${question.id}-${index}-${rowIndex}`}>
                    {
                      question.children?.map((q) => {
                        const childAnswers = answers[q.id] || [];
                        let answerIndex = childAnswers.findIndex(a => a && `${a.cell}` === rowCell);
                        if (answerIndex === -1) answerIndex = childAnswers.length;
                        return (
                          <td key={`subQuestionCell-${q.id}-${rowCell}`}>
                            {renderAnswerContainer({
                              question: q,
                              questionIndex: index,
                              isSubQuestion: true,
                              answerIndex: answerIndex,
                              rowCell: rowCell,
                              parentQuestion: question
                            })}
                          </td>
                        );
                      })
                    }
                    <td className="text-center subquestion-table-actions-cell">
                      <div className="d-flex align-items-center justify-content-start gap-2">
                        {
                          canReplicate && rowIndex === 0 &&
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {replicatedSubQuestionGroups[`${question?.id}_${rowCell}`]
                                  ? t('Clear.label')
                                  : t('CopyAnswerToSubQuestions.label')}
                              </Tooltip>
                            }>
                            <span className="cursor text-primary"
                              onClick={() => onReplicateSubQuestionAnswer(question, question.children[0], rowCell)}>
                              <BiCopy size={16} />
                            </span>
                          </OverlayTrigger>
                        }
                        {
                          question.children?.some(q => (answers[q.id] || []).some(a => a && `${a.cell}` === rowCell && a.id > 0)) &&
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t('Delete.label')}</Tooltip>}>
                            <span className='cursor text-danger'
                              onClick={() => onDeleteSubQuestion(question.children, rowCell)}>
                              <BiTrash size={16} />
                            </span>
                          </OverlayTrigger>
                        }
                        {
                          rowIndex === rowCells.length - 1 &&
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t('RepeatSuBQues.label')}</Tooltip>}>
                            <span className="cursor text-primary"
                              onClick={() => {
                                setRepeatClicked(true); // Update state on repeat button click
                                onRepeatSubQuestion(question);
                              }}>
                              <MdAddCircleOutline size={18} />
                            </span>
                          </OverlayTrigger>
                        }
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </Table>
        </div>
      </div>
    )
  };

  const onRepeatSubQuestion = (question) => {
    const ans = JSON.parse(JSON.stringify(answers));
    question?.children?.forEach(q => {
      const sortedAns = ans[q.id]?.sort((a, b) => +(a.cell) - +(b.cell));
      const nextCellIndex = +(sortedAns[sortedAns.length - 1].cell) + 1;
      ans[q.id].push({
        id: 0,
        answer: '',
        cell: `${nextCellIndex}`,
        edited: false
      });
    });
    setAnswers(ans);
  };

  const applyReplicateSubQuestionAnswer = (parentQuestion, sourceQuestion, rowCell, sourceAnswer) => {
    const groupKey = `${parentQuestion?.id}_${rowCell}`;
    const ans = JSON.parse(JSON.stringify(answers));
    parentQuestion?.children
      ?.filter(q => q.id !== sourceQuestion.id)
      ?.forEach(q => {
        if (!ans[q.id]) ans[q.id] = [];
        const idx = ans[q.id].findIndex(a => a && `${a.cell}` === rowCell);
        if (idx > -1) {
          ans[q.id][idx]['answer'] = sourceAnswer;
        } else {
          ans[q.id].push({ id: 0, answer: sourceAnswer, cell: rowCell, edited: false });
        }
      });
    setAnswers(ans);
    setReplicatedSubQuestionGroups({ ...replicatedSubQuestionGroups, [groupKey]: true });
  };

  const onReplicateSubQuestionAnswer = (parentQuestion, sourceQuestion, rowCell) => {
    const groupKey = `${parentQuestion?.id}_${rowCell}`;
    const isReplicated = !!replicatedSubQuestionGroups[groupKey];

    if (!isReplicated) {
      const sourceAnswer = answers[sourceQuestion.id]?.find(a => a && `${a.cell}` === rowCell)?.answer;
      if (!sourceAnswer) {
        toast.error(t('EnterYourAnswerHere.label'), {
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }

      const hasExistingAnswers = parentQuestion?.children
        ?.filter(q => q.id !== sourceQuestion.id)
        ?.some(q => !!answers[q.id]?.find(a => a && `${a.cell}` === rowCell)?.answer);

      if (hasExistingAnswers) {
        setPendingReplicateAnswer({ parentQuestion, sourceQuestion, rowCell, sourceAnswer });
        return;
      }

      applyReplicateSubQuestionAnswer(parentQuestion, sourceQuestion, rowCell, sourceAnswer);
    } else {
      const ans = JSON.parse(JSON.stringify(answers));
      parentQuestion?.children
        ?.filter(q => q.id !== sourceQuestion.id)
        ?.forEach(q => {
          const idx = ans[q.id]?.findIndex(a => a && `${a.cell}` === rowCell);
          if (idx === undefined || idx === -1) return;

          ans[q.id][idx]['answer'] = '';
        });

      setReplicatedSubQuestionGroups({ ...replicatedSubQuestionGroups, [groupKey]: false });
      setAnswers(ans);
    }
  };

  const onDeleteSubQuestion = (questions, rowCell) => {

    const ans = JSON.parse(JSON.stringify(answers));
    const reqs = [];
    const gdAnswerIdsByDisclosure = {};
    const metricAnswerIds = [];
    questions?.forEach(q => {
      const idx = answers[q.id]?.findIndex(a => a && `${a.cell}` === rowCell);
      if (idx === undefined || idx === -1) return;

      const a = answers[q.id][idx];
      if (a.id > 0) {
        if (type === 'MATERIAL_TOPIC') {
          metricAnswerIds.push(a.id);
        } else if (type === 'GENERAL_DISCLOSURE') {
          if (!gdAnswerIdsByDisclosure[q.disclosureId]) gdAnswerIdsByDisclosure[q.disclosureId] = [];
          gdAnswerIdsByDisclosure[q.disclosureId].push(a.id);
        }
      }
      ans[q.id].splice(idx, 1);
    });
    if (metricAnswerIds.length > 0) {
      reqs.push(CompanyService.deleteMetricAnswers(metricAnswerIds));
    }
    Object.keys(gdAnswerIdsByDisclosure).forEach(disclosureId => {
      reqs.push(GeneralDisclosureService.deleteAnswers(disclosureId, gdAnswerIdsByDisclosure[disclosureId]));
    });
    if (reqs?.length === 0) {
      setAnswers(ans);
      return;
    }
    Promise.all(reqs).then(res => {
      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
      setAnswers(ans);
      refreshData();
    }).catch(err => { });
  };

  const renderAnswerContainer = ({ question, questionIndex, isSubQuestion, answerIndex, rowCell, parentQuestion, showReplicateAction = false }) => {
    const isEmptyAnswer = !answers[question.id]
      || !answers[question.id][answerIndex]
      || !answers[question.id][answerIndex].answer;
    return (
      <>
        {
          answers[question.id] &&
            answers[question.id][answerIndex] &&
            answers[question.id][answerIndex].edited === true
            ?
            renderAnswerBox({
              question: question,
              index: questionIndex,
              isSubQuestion: isSubQuestion,
              answerIndex: answerIndex
            })
            :
            <div className='metricTopicAns_box cursor d-flex align-items-start justify-content-between w-100'>
              <div className="flex-grow-1" onClick={() => onEditAnswer(question.id, answerIndex, rowCell, parentQuestion)}>
                {
                  isEmptyAnswer
                    ? <div className="EnterYourAnswerHere_label">
                      {t('EnterYourAnswerHere.label')}</div>
                    : answers[question.id][answerIndex].answer
                }
              </div>
              <div className="metricTopicAns_actions" >
                {
                  isSubQuestion && showReplicateAction &&
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {replicatedSubQuestionGroups[`${parentQuestion?.id}_${rowCell}`]
                          ? t('Clear.label')
                          : t('CopyAnswerToSubQuestions.label')}
                      </Tooltip>
                    }
                  >
                    <span className="cursor text-primary px-2"
                      onClick={() => onReplicateSubQuestionAnswer(parentQuestion, question, rowCell)}>
                      <BiCopy size={16} />
                    </span>
                  </OverlayTrigger>
                }

                <OverlayTrigger placement="top" overlay={<Tooltip>{t('CommentsandDocumentsFile1.label')}</Tooltip>}>
                  <span className="cursor commentAttach_btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openCommentsAttachments(question, answerIndex ?? 0);
                    }}>
                    {
                      answers[question.id]?.[answerIndex]?.attachments?.length > 0 &&
                      <OverlayTrigger placement="top" overlay={<Tooltip>{t('AttachmentsAdded.label')}</Tooltip>}>
                        <span className="attachmentsFile_circle">

                        </span>
                      </OverlayTrigger>
                    }
                    <img src={commentAttached} />
                    {/* <BiDotsVerticalRounded size={20} /> */}
                  </span>
                </OverlayTrigger>



              </div>
            </div>
        }
      </>
    );
  };

  const onEditAnswer = (questionId, answerIndex, rowCell, parentQuestion) => {
    const ans = JSON.parse(JSON.stringify(answers));
    if (!ans[questionId]) ans[questionId] = [];
    const idx = answerIndex ?? 0;
    if (ans[questionId][idx]) {
      ans[questionId][idx]['edited'] = true;
    } else {
      let cellValue;
      if (rowCell != null && `${rowCell}`.trim() !== '') {
        cellValue = String(rowCell);
      } else {
        cellValue = String(idx + 1);
      }
      ans[questionId][idx] = {
        id: 0, answer: '', cell: cellValue, edited: true
      };
    }
    setAnswers(ans);
    const editedQues = JSON.parse(JSON.stringify(editedQuestion));
    editedQues[questionId] = { [idx]: true };
    setEditedQuestion(editedQues);
  };

  const cancelAnswerEdit = (questionId, answerIndex) => {
    const ans = JSON.parse(JSON.stringify(answers));
    ans[questionId][answerIndex]['edited'] = false;
    setAnswers(ans);
    const editedQues = JSON.parse(JSON.stringify(editedQuestion));
    editedQues[questionId] = { [answerIndex]: false };
    setEditedQuestion(editedQues);
  };
  const handleTextareaChange = (e) => {
    setTextEntered(e.target.value.trim() !== '');
  };
  const renderAnswerBox = ({ question, questionIndex, isSubQuestion, answerIndex }) => {
    const answerValue = isSubQuestion
      ? answers[question.id] ? answers[question.id][answerIndex]?.answer ?? '' : ''
      : answers[question.id] ? answers[question.id][0]?.answer ?? '' : '';
    return (
      <div id={`ansContainer-${question.id}-${questionIndex}-${answerIndex ?? 0}`}
        key={`ansContainer-${question.id}-${questionIndex}-${answerIndex ?? 0}`}
        className="answer-container">
        <div>
          <textarea
            rows={6}
            className='form-control editable'
            value={answerValue}
            disabled={question.disabled || !hasDMOneTimeUpdatePermission()}
            onChange={(e) => {
              handleTextareaChange(e);
              handleAnswerChange(question.id, answerIndex ?? 0, e.target.value);
            }}
          />
        </div>
        <div className='p-2'>
          {
            type === 'MATERIAL_TOPIC'
              ?

              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-esg-ai">{t("RefineYourResponseESGAICopilotToolTip.label")}</Tooltip>}>
                <button
                  type="button"
                  className="btn btn-ESGAi mx-2"
                  hidden={!hasDMOneTimeUpdatePermission()}
                  onClick={() => onRefineWithAIClick({
                    id: question.id,
                    materialtopic: question.metricName,
                    question: question.question,
                    guideline: question.helpText,
                    answer: answers[question.id] ? answers[question.id][answerIndex ?? 0]?.answer ?? '' : '',
                    questionCode: question.code
                  })}>
                  <img src={esg_ai} width={15} height={15} alt="ESG AI" />
                  {t("ESGAICopilot.label")}
                </button>
              </OverlayTrigger>

              : <></>
          }
          {
            !question.disabled &&
            <button
              type="button"
              className="btn btn-primary me-2"
              hidden={!hasDMOneTimeUpdatePermission()}
              onClick={() => saveIndividualAnswer(question, answerIndex ?? 0)}>
              {t("Save.label")}
            </button>
          }
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => cancelAnswerEdit(question.id, answerIndex ?? 0)}>
            {t("Close.label")}
          </button>
        </div>
      </div>
    )
  };

  const getRemarkAttachmentsFields = (ans) => {
    const fields = {};
    if (ans?.remark) {
      fields.remark = ans.remark;
    }
    if (ans?.attachments?.length > 0) {
      fields.attachments = ans.attachments.map(a => ({ id: a.id }));
    }
    return fields;
  };

  const saveAnswers = () => {
    const payload = [];
    const questionIds = Object.keys(answers);
    questionIds?.forEach(questionId => {
      answers[questionId]?.forEach(ans => {
        if (!ans.answer) return;

        payload.push({
          id: type === 'MATERIAL_TOPIC' ? (ans.id ?? 0) : (ans.id > 0 ? ans.id : null),
          questionId: +questionId,
          answer: ans.answer,
          cell: ans.cell,
          ...getRemarkAttachmentsFields(ans)
        });
      });
    });
    let req;
    if (type === 'MATERIAL_TOPIC') {
      req = CompanyService.postMetricAnswers(filter?.reportingPeriod, payload);
    } else {
      req = GeneralDisclosureService.saveAnswer(filter?.reportingPeriod, payload);
    }
    req?.then((res) => {
      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
      refreshData();
    }).catch((error) => { });
  };

  const saveIndividualAnswer = (question, answerIndex) => {
    const answer = answers[question.id][answerIndex];
    const payload = [{
      id: type === 'MATERIAL_TOPIC' ? (answer.id ?? 0) : (answer.id > 0 ? answer.id : null),
      questionId: +question.id,
      answer: answer.answer,
      cell: answer.cell,
      ...getRemarkAttachmentsFields(answer)
    }];
    let req;
    if (type === 'MATERIAL_TOPIC') {
      req = CompanyService.postMetricAnswers(filter?.reportingPeriod, payload);
    } else {
      req = GeneralDisclosureService.saveAnswer(filter?.reportingPeriod, payload);
    }

    req?.then((res) => {
      cancelAnswerEdit(question.id, answerIndex);
      refreshData();
    }).catch((error) => { });
  };

  const handleAnswerChange = (questionId, answerIndex, value) => {
    const ans = JSON.parse(JSON.stringify(answers));
    ans[questionId][answerIndex]['answer'] = value;
    setAnswers(ans);
  };

  const enabledOrDisableAnswering = (qId, aIndex, val) => {
    CompanyService.changeMetricQuestionStatus(qId, val).then(res => {
      if (res.status !== 200) return;

      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
      changeQuestionStatus(qId, val);
    }).catch(err => { });
  };

  const downloadAttchement = (data) => {
    if (data.externalId) {
      setLoaderDisplay(true);
      StandardService.getDocumentsAttached(data.externalId)
        .then((response) => {
          setLoaderDisplay(false);
          let fetchDataModified = `data:${response.data.mimeType};base64,${response.data.file}`;
          let link = document.createElement("a");
          link.href = fetchDataModified;
          link.download = response.data.name;
          link.click();
        })
        .catch((error) => {
          setLoaderDisplay(false);
          console.error('Error downloading file:', error);
        });
    } else {
      downloadAttchementAlternative(data.id);
    }
  };

  const downloadAttchementAlternative = (id) => {
    setLoaderDisplay(true);
    StandardService.getDocumentsAttachedAlt(id)
      .then((response) => {
        setLoaderDisplay(false);
        let fetchDataModified = `data:${response.data.mimeType};base64,${response.data.file}`;
        let link = document.createElement("a");
        link.href = fetchDataModified;
        link.download = response.data.name;
        link.click();
      })
      .catch((error) => {
        setLoaderDisplay(false);
        console.error('Error downloading file:', error);
      });
  };

  const openCommentsAttachments = (question, answerIndex) => {
    setCommentsAttachmentsFor({ question, answerIndex });
    setIsRemarkEdit(false);
    setTempRemark('');
    setTempRemarkAttachments([]);
  };

  const closeCommentsAttachments = () => {
    setCommentsAttachmentsFor(null);
    setIsRemarkEdit(false);
    setTempRemark('');
    setTempRemarkAttachments([]);
  };

  const onEditRemark = () => {
    const currentAnswer = answers[commentsAttachmentsFor.question.id]?.[commentsAttachmentsFor.answerIndex];
    setTempRemark(currentAnswer?.remark || '');
    setTempRemarkAttachments(currentAnswer?.attachments || []);
    setIsRemarkEdit(true);
  };

  const uploadRemarkAttachment = (fileUploaded) => {
    if (!fileUploaded) return;

    const formData = new FormData();
    formData.append("document", fileUploaded);
    setLoaderDisplay(true);
    StandardService.uploadAttachments(formData).then(res => {
      setLoaderDisplay(false);
      if (res.status === 200 || res.status === 201) {
        setTempRemarkAttachments(prev => [...prev, res.data]);
      }
    }).catch(err => {
      setLoaderDisplay(false);
      toast.error(t("QualitativeDataCardView.error.uploadFile.label"));
    });
  };

  const handleRemarkFileChange = (e) => {
    Array.from(e.target.files).forEach(file => uploadRemarkAttachment(file));
    if (remarkFileInputRef.current) {
      remarkFileInputRef.current.value = '';
    }
  };

  const removeRemarkAttachment = (fileId) => {
    setSelectedRemarkAttachmentId(fileId);
    setShowRemarkAttachmentConfirmation(true);
  };

  const confirmRemoveRemarkAttachment = () => {
    setTempRemarkAttachments(prev => prev.filter(f => f.id !== selectedRemarkAttachmentId));
    setShowRemarkAttachmentConfirmation(false);
    setSelectedRemarkAttachmentId(null);
  };

  const saveRemarkAndAttachments = () => {
    const { question, answerIndex } = commentsAttachmentsFor;
    const currentAnswer = answers[question.id]?.[answerIndex];
    const payload = {
      questionId: question.id,
      remark: tempRemark,
      attachments: tempRemarkAttachments.map(a => ({ id: a.id })),
      standardId: glbRegulationId
    };

    let req;
    if (type === 'MATERIAL_TOPIC') {
      payload.branchId = currentAnswer?.branchId;
      req = StandardService.saveBranchCommentsAttachments(filter?.reportingPeriod, payload);
    } else {
      req = GeneralDisclosureService.saveRemarkAttachments(filter?.reportingPeriod, payload);
    }

    setLoaderDisplay(true);
    req.then(res => {
      setLoaderDisplay(false);
      if (res.status !== 200) return;

      const ans = JSON.parse(JSON.stringify(answers));
      if (!ans[question.id]) ans[question.id] = [];
      if (!ans[question.id][answerIndex]) ans[question.id][answerIndex] = { id: 0, answer: '' };
      ans[question.id][answerIndex].remark = tempRemark;
      ans[question.id][answerIndex].attachments = tempRemarkAttachments;
      setAnswers(ans);

      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
      closeCommentsAttachments();
    }).catch(err => {
      setLoaderDisplay(false);
      toast.error(t("QualitativeDataCardView.error.saveComments.label"));
    });
  };

  const renderQuestionOptions = (q) => {
    return (
      <Dropdown>
        <Dropdown.Toggle
          variant="blank_outline"
          id="gdQuestionDropdown">
          <BiDotsVerticalRounded />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item
            onClick={() => setQuesOpenedForEdit(q)}>
            {t("Edit.label")}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <>
      {
        questions?.map((question, index) => {
          const qRef = createRef();
          questionRefs.push(qRef);
          const calculatedIndex = pgnum * 10 + index;
          return (
            <div className="question-container" key={`question-${calculatedIndex}`} id={`question-${calculatedIndex}`}>
              {renderQuestion({ question: question, index: calculatedIndex, qRef: qRef })}
            </div>
          );
        })
      }
      {
        questions?.length > 0 &&
        <div className="text-center mt-1">
          <button
            type="submit"
            className="btn btn-primary px-4"
            hidden={!hasDMOneTimeUpdatePermission()}
            onClick={() => { saveAnswers() }}>
            {t("Save.label")}
          </button>
        </div>
      }
      {
        quesOpenedForUpdate.id > 0 &&
        <GDQuestionFormPopup disclosureId={quesOpenedForUpdate.disclosureId}
          question={quesOpenedForUpdate}
          saveCallback={(val) => {
            setQuesOpenedForEdit({});
            if (val) {
              refreshData();
            }
          }} />
      }
      {
        commentsAttachmentsFor &&
        <Modal show={true}
          onHide={closeCommentsAttachments}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered backdrop="static"
          keyboard={false}>
          <Modal.Header closeButton>
            <Modal.Title as="h5">{t("CommentsandDocumentsFile.label")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="CommentsSection_box">
              <div className="CommentsSection_header">
                <span className="CommentsSection_label">{t("Comments.label")}</span>
                {!isRemarkEdit && (type === 'GENERAL_DISCLOSURE' || type === 'MATERIAL_TOPIC') && hasDMOneTimeUpdatePermission() && (
                  <OverlayTrigger placement="top" overlay={<Tooltip>{t("Edit.label")}</Tooltip>}>
                    <button className="CommentsSection_editBtn" role="button" onClick={onEditRemark}>
                      <BiPencil />
                    </button>
                  </OverlayTrigger>
                )}
              </div>
              {
                isRemarkEdit
                  ? <Form.Control
                    as="textarea"
                    rows={4}
                    className="CommentsSection_textarea"
                    value={tempRemark}
                    onChange={(e) => setTempRemark(e.target.value)}
                  />
                  : (
                    answers[commentsAttachmentsFor.question.id]?.[commentsAttachmentsFor.answerIndex]?.remark
                      ? <div className="CommentsSection_remark">{answers[commentsAttachmentsFor.question.id][commentsAttachmentsFor.answerIndex].remark}</div>
                      : <div className="CommentsSection_empty"><FcFinePrint className="me-2" /> No comments added</div>
                  )
              }
            </div>

            {isRemarkEdit && (
              <div className="CommentsSection_fileUpload">
                <label className='mb-1'>{t("QualitativeDataCardView.modal.files.label.label")}</label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleRemarkFileChange}
                  ref={remarkFileInputRef}
                />
              </div>
            )}

            <hr />
            <div className="p-1">
              <label className='mb-1'><b>{t("Documents&File.label")}</b></label>
              <div className='OCRattachedFiles_box'>
                {
                  (isRemarkEdit ? tempRemarkAttachments : answers[commentsAttachmentsFor.question.id]?.[commentsAttachmentsFor.answerIndex]?.attachments)?.length > 0
                    ? (isRemarkEdit ? tempRemarkAttachments : answers[commentsAttachmentsFor.question.id][commentsAttachmentsFor.answerIndex].attachments).map((file, fIndex) => (
                      <div key={`comment-attachment-${fIndex}`} className="OcrattachedFiles_boxIn">
                        <span className="OcrattachedFiles_icon">
                          <BsFileEarmarkFill />
                        </span>
                        <span
                          className="file-name"
                          role="button"
                          onClick={() => downloadAttchement(file)}>
                          {file.name}
                        </span>
                        {isRemarkEdit && (
                          <Button variant="Link" size="sm" className="OcrattachedFiles_removeBtn flex-shrink-0" onClick={() => removeRemarkAttachment(file.id)}>
                            <BsX />
                          </Button>
                        )}
                      </div>
                    ))
                    : <div className="OCRattachedFiles_empty"><FcFinePrint className="me-2" /> No attachments available!</div>
                }
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            {isRemarkEdit ? (
              <>
                <Button variant="secondary" onClick={() => setIsRemarkEdit(false)}>
                  {t("Cancel.label")}
                </Button>
                <Button variant="primary" onClick={saveRemarkAndAttachments}>
                  {t("Update.label")}
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={closeCommentsAttachments}>
                {t("Close.label")}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      }

      {showRemarkAttachmentConfirmation && (
        <ConfirmationModal
          modalShow={true}
          messageText={t("QualitativeDataCardView.confirmation.deleteFile.label")}
          callbackModal={(confirmed) => {
            setShowRemarkAttachmentConfirmation(false);
            if (confirmed) {
              confirmRemoveRemarkAttachment();
            } else {
              setSelectedRemarkAttachmentId(null);
            }
          }}
        />
      )}

      {pendingReplicateAnswer && (
        <ConfirmationModal
          modalShow={true}
          messageText={t("SomeQuestionsAlreadyAnswered.label")}
          confirmBtn={t("Yes.label")}
          callbackModal={(confirmed) => {
            const { parentQuestion, sourceQuestion, rowCell, sourceAnswer } = pendingReplicateAnswer;
            setPendingReplicateAnswer(null);
            if (confirmed) {
              applyReplicateSubQuestionAnswer(parentQuestion, sourceQuestion, rowCell, sourceAnswer);
            }
          }}
        />
      )}
    </>
  );
}

export default withTranslation()(CompanyQuestionContainer);
